import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, MessageCircle, Send, X, Bot, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const QUICK_PROMPTS = [
  "What services do you offer?",
  "Show me your case studies",
  "I want to book a discovery call",
  "How do you price projects?",
];

const QUICK_REPLIES = {
  "What services do you offer?":
    "We deliver AI-powered mobile apps, websites, Salesforce automation with AI agents, and LLM workflows. Want a tailored intro for your use case? Share your details below.",
  "Show me your case studies":
    "We've reduced fraud losses by 80%, saved 2,000+ analyst hours, and cut investigation time by 60%. Visit /case-studies for details. Drop your details below and we'll send a curated brief.",
  "I want to book a discovery call":
    "Awesome — fill in your details below and our team will reach out within 24 hours to schedule a 30-min call.",
  "How do you price projects?":
    "We scope per-engagement based on outcomes. Typical pilots start at 4-6 weeks. Share a few details and we'll send a tailored estimate.",
};

const initialMessages = [
  { type: "bot", text: "Hi! I'm Nova, your AI guide at Namespace." },
  { type: "bot", text: "Ask me anything about our services, or drop your details and our team will reach out within 24 hours." },
];

const SupportBot = () => {
  const [open, setOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState(initialMessages);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", description: "", website: "" });
  const messagesEndRef = useRef(null);

  const nameRegex = /^[A-Za-z\s]*$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const phoneRegex = /^[0-9]{7,15}$/;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, isTyping]);

  const sendQuickPrompt = (prompt) => {
    setMessages((prev) => [...prev, { type: "user", text: prompt }]);
    setIsTyping(true);
    setTimeout(() => {
      setMessages((prev) => [...prev, { type: "bot", text: QUICK_REPLIES[prompt] || "Thanks! Tell us more below." }]);
      setIsTyping(false);
      setShowForm(true);
    }, 800);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!nameRegex.test(form.name)) return toast.error("Name should contain only letters");
    if (!form.email.trim()) return toast.error("Email is required");
    if (!emailRegex.test(form.email)) return toast.error("Invalid email address");
    if (form.phone && !phoneRegex.test(form.phone)) return toast.error("Phone should be 7-15 digits");

    setIsTyping(true);
    setMessages((prev) => [...prev, { type: "user", text: `${form.name} • ${form.email}` }]);

    try {
      await axios.post(`${API}/contact/send`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: "Contact request from AI Assistant",
        source: "AI Assistant",
        description: form.description,
        website: form.website,
      });
    } catch (e) {
      // Best effort; we still confirm to the user.
    }

    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        { type: "bot", text: `Thanks ${form.name.split(" ")[0]}! We've received your request.` },
        { type: "bot", text: "Our team will reach out within 24 hours. Anything else I can help with?" },
      ]);
      setIsTyping(false);
      setForm({ name: "", phone: "", email: "", description: "", website: "" });
      setShowForm(false);
    }, 1200);
  };

  return (
    <div className="fixed bottom-20 right-4 sm:right-6 z-[70]" data-testid="support-bot">
      <AnimatePresence>
        {open ? (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="w-[340px] sm:w-[380px] rounded-3xl overflow-hidden shadow-2xl border border-[color:var(--border-default)] bg-[color:var(--surface-1)]/95 backdrop-blur-2xl"
            style={{ boxShadow: "0 24px 80px rgba(147, 51, 234, 0.25), 0 0 0 1px rgba(255,255,255,0.04) inset" }}
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-[#7C3AED] via-[#9333EA] to-[#22D3EE] p-4">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.20),transparent_60%)]" />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative w-10 h-10 rounded-2xl bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/20 animate-pulse-glow">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-white inline-flex items-center gap-2">
                      Nova
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-white/20 text-white">AI</span>
                    </p>
                    <p className="text-[11px] text-white/80 inline-flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-300" />
                      Online · Avg reply 24h
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="hover:bg-white/20 rounded-full p-1.5 transition-colors text-white"
                  aria-label="Minimize chat"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="h-72 overflow-y-auto p-4 space-y-3 bg-[color:var(--surface-1)]">
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}
                >
                  {msg.type === "bot" && (
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#9333EA] to-[#22D3EE] flex items-center justify-center mr-2 flex-shrink-0">
                      <Bot className="w-3.5 h-3.5 text-white" />
                    </div>
                  )}
                  <div
                    className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 text-xs leading-relaxed ${
                      msg.type === "user"
                        ? "bg-gradient-to-br from-[#7C3AED] to-[#9333EA] text-white rounded-br-md"
                        : "bg-[color:var(--surface-2)] text-[var(--text-primary)] border border-[color:var(--border-subtle)] rounded-bl-md"
                    }`}
                  >
                    {msg.text}
                  </div>
                </motion.div>
              ))}

              {isTyping && (
                <div className="flex justify-start">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#9333EA] to-[#22D3EE] flex items-center justify-center mr-2 flex-shrink-0">
                    <Bot className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div className="bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] rounded-2xl rounded-bl-md px-4 py-3 inline-flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-primary-light)] typing-dot" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-primary-light)] typing-dot" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-primary-light)] typing-dot" />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Quick prompts */}
            {!showForm && (
              <div className="px-4 pb-3 bg-[color:var(--surface-1)] border-t border-[color:var(--border-subtle)]">
                <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mt-3 mb-2 font-semibold">
                  Suggested
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {QUICK_PROMPTS.map((p) => (
                    <button
                      key={p}
                      onClick={() => sendQuickPrompt(p)}
                      className="text-[11px] px-2.5 py-1.5 rounded-full border border-[color:var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[color:var(--border-brand)] hover:bg-[color:var(--brand-primary)]/10 transition-all"
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowForm(true)}
                  className="mt-3 w-full text-xs btn-primary rounded-full h-9"
                >
                  Get in touch →
                </button>
              </div>
            )}

            {/* Form */}
            {showForm && (
              <div className="p-4 space-y-2 bg-[color:var(--surface-1)] border-t border-[color:var(--border-subtle)]">
                <Input
                  placeholder="Your Name *"
                  value={form.name}
                  onChange={(e) => {
                    let v = e.target.value;
                    if (!nameRegex.test(v)) return;
                    if (v.length === 1) v = v.toUpperCase();
                    setForm({ ...form, name: v });
                  }}
                  className="bg-[color:var(--surface-2)] border-[color:var(--border-default)] text-xs h-8 focus:border-[color:var(--brand-primary)]"
                />
                <Input
                  placeholder="Email *"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-[color:var(--surface-2)] border-[color:var(--border-default)] text-xs h-8 focus:border-[color:var(--brand-primary)]"
                />
                <Input
                  placeholder="Phone (optional)"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "") })}
                  className="bg-[color:var(--surface-2)] border-[color:var(--border-default)] text-xs h-8 focus:border-[color:var(--brand-primary)]"
                />
                <Textarea
                  placeholder="What can we help with?"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="bg-[color:var(--surface-2)] border-[color:var(--border-default)] text-xs focus:border-[color:var(--brand-primary)] resize-none"
                  rows={2}
                />

                {/* Honeypot */}
                <div className="absolute left-[-10000px] w-1 h-1 overflow-hidden" aria-hidden="true">
                  <Input
                    name="website"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => setShowForm(false)}
                    className="flex-1 text-xs h-9 rounded-full border border-[color:var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                  >
                    Back
                  </button>
                  <Button onClick={handleSubmit} className="flex-1 btn-primary text-xs rounded-full h-9 inline-flex items-center justify-center gap-1.5">
                    <Send className="w-3 h-3" /> Send
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.button
            key="trigger"
            initial={{ scale: 0, rotate: -45 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 16 }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.94 }}
            onClick={() => setOpen(true)}
            className="relative w-14 h-14 rounded-full bg-gradient-to-br from-[#7C3AED] via-[#9333EA] to-[#22D3EE] flex items-center justify-center shadow-xl text-white animate-pulse-glow"
            aria-label="Open AI assistant"
            style={{ boxShadow: "0 12px 40px rgba(147, 51, 234, 0.45)" }}
          >
            <MessageCircle className="w-6 h-6" />
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[color:var(--page-bg)]" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SupportBot;
